import { useState } from 'react'
import { useRotateAPIKey } from '../api/hooks'
import { ApiError } from '../api/client'
import { getAuthUser } from '../auth'

export default function SettingsPage() {
  const user = getAuthUser()
  const rotateKey = useRotateAPIKey()
  const [newKey, setNewKey] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  async function handleRotate() {
    setError('')
    try {
      const res = await rotateKey.mutateAsync()
      setNewKey(res.api_key)
      setConfirmed(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to rotate key')
    }
  }

  return (
    <div className="px-8 py-6 max-w-2xl">
      <h1 className="text-xl font-semibold text-gray-900 mb-6">Settings</h1>

      {/* Account info */}
      <section className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm mb-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Account</h2>
        <dl className="space-y-2 text-sm">
          <div className="flex gap-4">
            <dt className="text-gray-400 w-28">Tenant ID</dt>
            <dd className="font-mono text-gray-900">{user?.tenantId}</dd>
          </div>
          <div className="flex gap-4">
            <dt className="text-gray-400 w-28">User ID</dt>
            <dd className="font-mono text-gray-900">{user?.userId}</dd>
          </div>
          <div className="flex gap-4">
            <dt className="text-gray-400 w-28">Role</dt>
            <dd className="text-gray-900">{user?.role}</dd>
          </div>
        </dl>
      </section>

      {/* API Key rotation */}
      <section className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700 mb-1">Ingest API Key</h2>
        <p className="text-xs text-gray-400 mb-4">
          The API key is used in the <code className="bg-gray-100 px-1 rounded">X-API-Key</code> header
          for ingest endpoints. Rotating invalidates the current key immediately.
        </p>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}

        {newKey ? (
          <div className="space-y-3">
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              New key generated. Save it now — it won't be shown again.
            </p>
            <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono break-all select-all">
              {newKey}
            </code>
            <button
              onClick={() => setNewKey(null)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {!confirmed ? (
              <button
                onClick={() => setConfirmed(true)}
                className="text-sm font-medium text-red-600 hover:text-red-700 border border-red-200 hover:border-red-300 rounded-lg px-4 py-2 transition-colors"
              >
                Rotate API Key…
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-600">This will invalidate your current key. Continue?</p>
                <button
                  onClick={handleRotate}
                  disabled={rotateKey.isPending}
                  className="text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
                >
                  {rotateKey.isPending ? 'Rotating…' : 'Confirm'}
                </button>
                <button
                  onClick={() => setConfirmed(false)}
                  className="text-sm text-gray-400 hover:text-gray-600"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
